import { useSelector } from 'react-redux';
import { Button, Caret, DropdownMenu, Subtitle } from '../../components/shared';
import { IOysterLineDetail } from '../../entities/farms.entities';
import { ICycleResource } from '../../entities/growing.entities';
import { formatNumber } from '../../entities/util-functions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { Spin } from 'antd';
import {
  SelectIsEditable,
  SelectIsFarmable,
  SelectIsGrower,
} from '../../store/extra/extra.selector';
import {
  defaultDateFormat,
  amountDays,
  displayValueUnit,
} from '../../lib/common.helpers';
import metaIcon from '../../images/history_icon.png';
import { selectAccount } from '../../store/auth/auth.selector';
import { SeedSizeRange } from '../../components/CommonViews';

interface Props {
  lineData: IOysterLineDetail;
  cyclePart?: ICycleResource | null;
  isPrevEnabled: boolean;
  isNextEnabled: boolean;
  onClickPrevGroup: () => void;
  onClickNextGroup: () => void;
  showEditLineModal: () => void;
  onSeedingClick: () => void;
  onAssessmentClick: () => void;
  onCatchSpatClick: () => void;
  onHarvestClick: () => void;
  onReturnClick: () => void;
  onMaintenanceClick: () => void;
  onExtraSeedClick: () => void;
  onFloatingManageClick(): void;
  onMetaEditClick: () => void;
  checkedHistoryView: number;
  onHistoryViewClick: () => void;
}

const OysterLineMobile = ({
  cyclePart,
  lineData,
  isPrevEnabled,
  isNextEnabled,
  onClickPrevGroup,
  onClickNextGroup,
  showEditLineModal,
  onSeedingClick,
  onAssessmentClick,
  onCatchSpatClick,
  onHarvestClick,
  onReturnClick,
  onMaintenanceClick,
  onExtraSeedClick,
  onFloatingManageClick,
  onMetaEditClick,
  checkedHistoryView,
  onHistoryViewClick,
}: Props) => {
  const lang = useSelector(selectLang);
  const isEditable = SelectIsEditable(lineData.farm_id);
  const isFarmable = SelectIsFarmable(lineData.farm_id);
  const isGrower = SelectIsGrower(lineData.farm_id);
  const account = useSelector(selectAccount);
  const unitType = account?.display_unit;
  const mainSeed = cyclePart?.main_seed,
    lastAssessment =
      cyclePart && cyclePart.assessments.length > 0
        ? cyclePart.assessments.reduce(
            (p, c) => (p.assessment_date < c.assessment_date ? c : p),
            cyclePart.assessments[0],
          )
        : null;
  const growingBaskets = lineData.growing_cycle
    ? lineData.growing_cycle.current_basket_count
    : 0;

  return (
    <>
      <div className='pt-16 pb-4'>
        <div className={'table-mobile__card'} onKeyDown={() => undefined}>
          {isEditable && (
            <div className={'table-mobile__card-dots'}>
              <DropdownMenu
                data={lineData}
                onEdit={showEditLineModal}
                column='isFarm'
                isRedirect={`/farms/${lineData.farm_id}`}
              />
            </div>
          )}
          <div className='d-flex justify-between align-center'>
            <Subtitle size={5} color='black-3' align='left' fontWeight={600}>
              {translate(lang, 'Line - %s', lineData.line_name)}
            </Subtitle>
          </div>
          <div className='pt-16'>
            <div className='d-flex pb-23'>
              <div className='flex-basis-50'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Number of %s', lineData.farming_method)}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  {lineData.growing_cycle
                    ? `${growingBaskets}/${
                        lineData.basket_count
                      } (${formatNumber(
                        (growingBaskets * 100) / lineData.basket_count,
                      )}%)`
                    : `-/${lineData.basket_count}`}
                </Subtitle>
              </div>
              <div className='flex-basis-50 ml-24'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Date seeded')}
                </Subtitle>
                {mainSeed ? (
                  <Subtitle
                    size={5}
                    color='black-5'
                    align='left'
                    fontWeight={400}
                  >
                    {defaultDateFormat(mainSeed.planned_date_seed)}
                  </Subtitle>
                ) : (
                  <div className='d-flex flex-wrap'>
                    <Subtitle
                      size={5}
                      color='black-5'
                      align='left'
                      fontWeight={400}
                      className='pr-4'
                    >
                      {translate(lang, 'Line empty for')}
                    </Subtitle>
                    <Subtitle
                      size={5}
                      color='black-5'
                      align='left'
                      fontWeight={600}
                    >
                      {amountDays(lineData.line_idle, lang)}
                    </Subtitle>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex pb-23'>
              <div className='flex-basis-50'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Planned harvest date')}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  {defaultDateFormat(
                    lastAssessment?.planned_date_harvest ??
                      mainSeed?.planned_date_harvest,
                    '-',
                  )}
                </Subtitle>
              </div>
              <div className='flex-basis-50 ml-24'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Line Length')}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  {displayValueUnit(lineData.backbone, unitType, 'distance_m')}
                </Subtitle>
              </div>
            </div>

            <div className='d-flex pb-23'>
              <div className='flex-basis-50'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Seeded Spat Size')}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  <SeedSizeRange data={mainSeed} />
                </Subtitle>
              </div>
              <div className='flex-basis-50 ml-24'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Avg. Size')}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  {lastAssessment ? (
                    displayValueUnit(
                      lastAssessment.shell_size.avg,
                      unitType,
                      'size_mm',
                      '-',
                    )
                  ) : (
                    <SeedSizeRange data={mainSeed} />
                  )}
                </Subtitle>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='white-card pt-16 pl-12 pb-16 pr-12 mb-8'>
        <div className='d-flex justify-between'>
          <div className='w-100 d-flex justify-between align-center  white-card-small pt-3 pb-3'>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='mr-26'
              name='prev'
              onClick={onClickPrevGroup}
              disabled={!isPrevEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='left' />
            </Button>
            <Subtitle
              size={4}
              color='black'
              align='left'
              fontWeight={500}
              disabled={!mainSeed}
            >
              {mainSeed?.season_name ?? 'Empty'}
            </Subtitle>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='ml-26'
              name='next'
              onClick={onClickNextGroup}
              disabled={!isNextEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='right' />
            </Button>
          </div>
          <Button
            color='blue'
            size={0}
            width='default'
            type='bordered'
            className='ml-12'
            iconOnly
            onClick={onMetaEditClick}
            disabled={!isEditable}
          >
            <img src={metaIcon} alt='meta-icon' style={{ width: 40 }} />
          </Button>
        </div>
        <div className='pt-16'>
          {(mainSeed || lineData.seedings.length > 0) && (
            <button
              className={`timeline-button${
                checkedHistoryView === 1 ? ' --checked' : ''
              }`}
              onClick={onHistoryViewClick}
            >
              {checkedHistoryView === -1 ? (
                <div className='ml-17 mr-17'>
                  <Spin />
                </div>
              ) : (
                translate(
                  lang,
                  checkedHistoryView === 1 ? 'Hide history' : 'Show history',
                )
              )}
            </button>
          )}
          {!!mainSeed?.is_growing ? (
            <>
              <Button
                className='mb-8 mt-8'
                color='blue'
                size={1}
                width='wide'
                type='fill'
                onClick={onFloatingManageClick}
                disabled={!isFarmable}
              >
                {translate(lang, 'Inventory Management')}
              </Button>
              <Button
                color='blue'
                size={1}
                width='wide'
                type='fill'
                onClick={onAssessmentClick}
                disabled={!isFarmable}
              >
                {translate(lang, 'Add assessment')}
              </Button>
              {!mainSeed.is_catch_spat && (
                <Button
                  className='mt-8'
                  color='blue'
                  size={1}
                  width='wide'
                  type='fill'
                  onClick={onExtraSeedClick}
                  disabled={!isFarmable}
                >
                  {translate(lang, 'Add extra seed')}
                </Button>
              )}
              <Button
                className='mt-8'
                color='green'
                size={1}
                width='wide'
                type='fill'
                onClick={onMaintenanceClick}
                disabled={!isFarmable}
              >
                {translate(lang, 'Add maintenance')}
              </Button>
              <Button
                color='blue'
                size={1}
                width='wide'
                type='bordered'
                className='mt-8'
                onClick={onHarvestClick}
                disabled={!isFarmable}
              >
                {translate(lang, 'Harvest Complete')}
              </Button>
              {!!account?.have_customers && (
                <Button
                  color='blue'
                  size={1}
                  width='wide'
                  type='bordered'
                  className='mt-8'
                  onClick={onReturnClick}
                  disabled={!mainSeed?.is_growing || !isFarmable}
                >
                  {translate(lang, 'Spat Harvest')}
                </Button>
              )}
            </>
          ) : (
            <>
              {!isGrower && (
                <Button
                  color='blue'
                  size={1}
                  width='wide'
                  type='fill'
                  onClick={onCatchSpatClick}
                  disabled={!isFarmable}
                >
                  {translate(lang, 'Catch Spat')}
                </Button>
              )}
              <Button
                className='mt-10'
                color='blue'
                size={1}
                width='wide'
                type='fill'
                onClick={onSeedingClick}
                disabled={!isFarmable}
              >
                {translate(lang, 'Seed the line')}
              </Button>
              <div className='d-flex justify-center align-center mt-10'>
                <Subtitle size={5} color='black' align='left' fontWeight={400}>
                  {translate(lang, 'Line empty for')}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black'
                  align='left'
                  fontWeight={600}
                  className='ml-4 mr-27'
                >
                  {amountDays(lineData?.line_idle, lang)}
                </Subtitle>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OysterLineMobile;
